import React, { useMemo } from "react";
import {
  DashUnitsNetProfitChartCanvasSkeleton,
  DashUnitsNetProfitChartFooterSkeleton,
} from "./DashCardSkeletons";
import { useThemeMode } from "../../../../contexts/ThemeContext";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  LineController,
  BarController,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { getAmount } from "../../../../utils/helper";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  LineController,
  BarController,
  ChartTooltip,
  Legend,
  Filler
);

function addCommas(n) {
  return String(Math.round(Number(n) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatShortUnits(n) {
  const v = Number(n) || 0;
  if (v >= 100000) return `${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return addCommas(v);
}

// ONLY for y-axis (right side) - formats net profit with K/L
function formatChartDateLabel(dateStr) {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    const [year, month] = dateStr.split("-");
    return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString("en-GB", {
      month: "short",
      year: "numeric",
    });
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-");
    return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });
  }
  return String(dateStr);
}

function formatNetProfitAxis(value, currencySign = "") {
  const v = Math.abs(Number(value) || 0);
  const sign = Number(value) < 0 ? "-" : "";
  if (v >= 100000) {
    return `${sign}${(v / 100000).toFixed(2)}L`;
  }
  if (v >= 1000) {
    return `${sign}${(v / 1000).toFixed(1)}K`;
  }
  return `${sign}${addCommas(v)}`;
}

const CHART_PALETTE = {
  light: {
    tick: "#a1a5b7",
    grid: "rgba(228, 230, 239, 0.85)",
    unitsBarBg: "rgba(49, 79, 113, 0.19)",
    unitsBarBorder: "#314f71",
    netLine: "#F45B68",
    netFill: "rgba(244, 91, 104, 0.08)",
    pointBg: "#ffffff",
    tooltipBg: "#ffffff",
    tooltipTitle: "#181c32",
    tooltipBody: "#3f4254",
    tooltipBorder: "#e4e6ef",
  },
  dark: {
    tick: "#a1a5b7",
    grid: "rgba(161, 165, 183, 0.32)",
    unitsBarBg: "rgba(110, 168, 254, 0.22)",
    unitsBarBorder: "#6ea8fe",
    netLine: "#F45B68",
    netFill: "rgba(244, 91, 104, 0.18)",
    pointBg: "#1e1e2d",
    tooltipBg: "#2b2b40",
    tooltipTitle: "#e1e3ea",
    tooltipBody: "#a1a5b7",
    tooltipBorder: "#3f4254",
  },
};

export default function DashUnitsNetProfitChartCard({
  loading,
  series,
  totals,
  currencySign = "₹",
  periodicity = "day",
  isMobile,
}) {
  const { themeMode } = useThemeMode();
  const palette = CHART_PALETTE[themeMode === "dark" ? "dark" : "light"];

  const units = series?.units || [];
  const net = series?.net || [];
  const dates = series?.dates || [];

  const chartPayload = useMemo(() => {
    const n = Math.min(units.length, net.length, dates.length || units.length);
    if (n === 0) return null;
    const u = units.slice(0, n).map((v) => Number(v) || 0).reverse();
    const ne = net.slice(0, n).map((v) => Number(v) || 0).reverse();
    const rawDates = dates.slice(0, n).reverse();
    const labels = rawDates.length
      ? rawDates.map((d) => formatChartDateLabel(d) || "")
      : u.map((_, i) => `D${i + 1}`);
    return { labels, rawDates, units: u, net: ne, n };
  }, [units, net, dates]);

  const footer = useMemo(() => {
    const n = chartPayload?.n || 0;
    const u = chartPayload?.units || [];
    if (!n) return null;
    const totalU = u.reduce((a, b) => a + b, 0);
    const avgDay = n > 0 ? totalU / n : 0;
    const best = Math.max(...u, 0);
    const netTotal = totals?.net ?? 0;
    const netLakh = Math.abs(netTotal) >= 100000;
    const netStr = netLakh
      ? `${netTotal < 0 ? "-" : ""}${currencySign}${(Math.abs(netTotal) / 100000).toFixed(2)}L`
      : getAmount(netTotal, currencySign, { decimals: 2 });
    return {
      totalUnits: formatShortUnits(totalU),
      avgDay: `${Math.round(avgDay)} u`,
      bestDay: `${Math.round(best)} u`,
      netProfit: netStr,
    };
  }, [chartPayload, totals?.net, currencySign]);

  const subtitle =
    periodicity === "month" ? "Monthly breakdown" : "Daily breakdown — MTD";

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: palette.tooltipBg,
          titleColor: palette.tooltipTitle,
          bodyColor: palette.tooltipBody,
          borderColor: palette.tooltipBorder,
          borderWidth: 1,
          padding: 10,
          callbacks: {
            title(items) {
              const idx = items[0]?.dataIndex;
              const raw = chartPayload?.rawDates?.[idx];
              if (raw) return formatChartDateLabel(raw);
              return items[0]?.label ?? "";
            },
            label(ctx) {
              const ds = ctx.dataset;
              const v = ctx.parsed?.y;
              if (ds.label === "Units") return ` Units: ${addCommas(v)}`;
              return ` Net profit: ${getAmount(v, currencySign)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            maxRotation: 0,
            autoSkip: false,
            color: palette.tick,
            font: { size: 11, weight: "500" },
            callback(value, index) {
              const chart = this.chart;
              const labels = chart?.data?.labels ?? [];
              if (labels.length === 0) return "";
              
              // Show fewer labels on mobile/desktop as needed
              const marks = new Set(
                [0, 7, 14, 21, labels.length - 1].filter(i => i >= 0 && i < labels.length)
              );
              
              return marks.has(index) ? labels[index] : "";
            },
          },
        },
        y: {
          type: "linear",
          position: "left",
          beginAtZero: true,
          title: { display: false },
          grid: { color: palette.grid, lineWidth: 1 },
          ticks: {
            color: palette.tick,
            font: { size: 10 },
            maxTicksLimit: isMobile ? 6 : 10,
            callback: (v) => `${v}`,
          },
        },
        y1: {
          type: "linear",
          position: "right",
          beginAtZero: true,
          grid: { drawOnChartArea: false },
          ticks: {
            color: palette.tick,
            font: { size: 10 },
            // ONLY y-axis uses K/L format
            callback: (v) => formatNetProfitAxis(v, currencySign),
          },
        },
      },
    }),
    [currencySign, chartPayload, isMobile, palette]
  );

  const data = useMemo(() => {
    if (!chartPayload) return { labels: [], datasets: [] };
    return {
      labels: chartPayload.labels,
      datasets: [
        {
          type: "bar",
          label: "Units",
          data: chartPayload.units,
          yAxisID: "y",
          backgroundColor: palette.unitsBarBg,
          borderColor: palette.unitsBarBorder,
          borderWidth: { top: 2, bottom: 0, left: 0, right: 0 },
          borderSkipped: false,
          maxBarThickness: 22,
          order: 2,
        },
        {
          type: "line",
          label: "Net Profit",
          data: chartPayload.net,
          yAxisID: "y1",
          borderColor: palette.netLine,
          backgroundColor: palette.netFill,
          fill: true,
          tension: 0.35,
          pointBackgroundColor: palette.pointBg,
          pointBorderColor: palette.netLine,
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 5,
          borderWidth: 2,
          order: 1,
        },
      ],
    };
  }, [chartPayload, palette]);

  return (
    <div className="dash-units-net-chart-card card border shadow-none h-100">
      <div className="card-body d-flex flex-column p-5 pb-4">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
          <div>
            <h3 className="dash-units-net-chart-card__title fw-bolder mb-1 fs-4">
              Units Sold &amp; Net Profit
            </h3>
            <p className="dash-units-net-chart-card__subtitle fs-7 fw-semibold mb-0">{subtitle}</p>
          </div>
          <div className="d-flex align-items-center gap-4 flex-shrink-0">
            <span className="dash-units-net-chart-card__legend d-flex align-items-center gap-2 fs-7 fw-semibold">
              <span
                className="dash-units-net-chart-card__legend-swatch--net rounded-1 flex-shrink-0"
                aria-hidden
              />
              Net Profit
            </span>
            <span className="dash-units-net-chart-card__legend d-flex align-items-center gap-2 fs-7 fw-semibold">
              <span
                className="dash-units-net-chart-card__legend-swatch--units flex-shrink-0 rounded-1"
                aria-hidden
              />
              Units
            </span>
          </div>
        </div>

        <div className="dash-units-net-chart-card__canvas flex-grow-1 position-relative" style={{ minHeight: 180 }}>
          {loading ? (
            <DashUnitsNetProfitChartCanvasSkeleton />
          ) : chartPayload ? (
            <Chart key={themeMode} type="bar" data={data} options={options} />
          ) : (
            <div className="dash-units-net-chart-card__empty d-flex align-items-center justify-content-center h-100 fs-7">
              No data for this range
            </div>
          )}
        </div>

        {loading ? (
          <div className="dash-units-net-chart-card__footer border-top border-gray-200 pt-5 mt-2">
            <DashUnitsNetProfitChartFooterSkeleton />
          </div>
        ) : null}

        {footer && !loading ? (
          <div className="dash-units-net-chart-card__footer border-top border-gray-200 pt-5 mt-2">
            <div className="row g-3 text-center text-md-start">
              <div className="col-3 col-md-3">
                <div className="dash-units-net-chart-card__ft-label">TOTAL UNITS</div>
                <div className="dash-units-net-chart-card__ft-value dash-units-net-chart-card__ft-value--blue">
                  {footer.totalUnits}
                </div>
              </div>
              <div className="col-3 col-md-3">
                <div className="dash-units-net-chart-card__ft-label">AVG/DAY</div>
                <div className="dash-units-net-chart-card__ft-value">{footer.avgDay}</div>
              </div>
              <div className="col-3 col-md-3">
                <div className="dash-units-net-chart-card__ft-label">BEST DAY EST.</div>
                <div className="dash-units-net-chart-card__ft-value">{footer.bestDay}</div>
              </div>
              <div className="col-3 col-md-3">
                <div className="dash-units-net-chart-card__ft-label">NET PROFIT</div>
                <div className="dash-units-net-chart-card__ft-value dash-units-net-chart-card__ft-value--profit">
                  {footer.netProfit}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}